/**
 * OAuth & Integration API Service
 *
 * Handles OAuth flows and integration status for external services
 */

import axios from 'axios';
import { config } from '@/src/config';

const API_URL = config.weaveinApi.baseUrl;

// Helper to get auth headers
const getAuthHeaders = () => {
  const token = localStorage.getItem('auth_token');
  const cboId = localStorage.getItem('active_cbo_id');
  return {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` }),
    ...(cboId && { 'X-CBO-ID': cboId })
  };
};

// Types for OAuth and integrations
/** What a personal Google / Microsoft connection was granted (TRUS-638). */
export interface ConnectionCapabilities {
  mailbox?: boolean;
  send_mail?: boolean;
  calendar?: boolean;
  contacts?: boolean;
  /** Microsoft only (Teams meetings). */
  meetings?: boolean;
}

export interface IntegrationStatus {
  connected: boolean;
  email?: string;
  expires_at?: string;
  connected_at?: string;
  updated_at?: string;
  /** Personal connections belong to the user and serve every org they work in. */
  scope?: 'user';
  /**
   * Derived from the scopes the user granted. null/undefined = unknown (a
   * connection made before scopes were recorded; reconnecting fills it in).
   */
  capabilities?: ConnectionCapabilities | null;
}

export interface CalendarSyncConfig {
  id: number;
  provider: 'google' | 'microsoft';
  external_calendar_id: string | null;
  external_calendar_name: string | null;
  sync_direction: 'inbound' | 'outbound' | 'bidirectional';
  sync_enabled: boolean;
  /** On and the last run did not fail. */
  is_active: boolean;
  /** ok | pending (never ran) | failing (see last_sync_error) | off (TRUS-694). */
  state: 'ok' | 'pending' | 'failing' | 'off';
  last_sync_at: string | null;
  last_sync_status: string | null;
  last_sync_error: string | null;
  has_sync_token: boolean;
}

export interface CalendarPullResult {
  success: boolean;
  message: string;
  details?: { total?: number; [k: string]: unknown };
}

export interface IntegrationStatuses {
  zoom: IntegrationStatus;
  google: IntegrationStatus;
  microsoft: IntegrationStatus;
  asana: IntegrationStatus;
}

export interface MeetingProvider {
  provider_type: string;
  name: string;
  is_active: boolean;
  is_default: boolean;
  has_credentials: boolean;
}

export interface OrgIntegrationStatus {
  providers: MeetingProvider[];
}

export interface AuthUrlResponse {
  auth_url: string;
}

export interface DisconnectResponse {
  success: boolean;
}

/** Personal calendar sync (GET/POST /api/calendar/sync/*). */
export const calendarSyncAPI = {
  getConfigs: async (): Promise<CalendarSyncConfig[]> => {
    const response = await axios.get(`${API_URL}/api/calendar/sync/config`, { headers: getAuthHeaders() });
    return response.data?.configs ?? [];
  },
  /** Import events from the provider's calendar (primary unless configured). */
  pull: async (provider: 'google' | 'microsoft'): Promise<CalendarPullResult> => {
    const response = await axios.post(`${API_URL}/api/calendar/sync/pull/${provider}`, null, { headers: getAuthHeaders() });
    return response.data;
  },
};

// OAuth API
export const oauthAPI = {
  // Get integration status for all providers
  getStatus: async (): Promise<IntegrationStatuses> => {
    const response = await axios.get(`${API_URL}/api/oauth/status`, { headers: getAuthHeaders() });
    return response.data;
  },

  // Get org-level meeting provider status
  getOrgStatus: async (): Promise<OrgIntegrationStatus> => {
    const response = await axios.get(`${API_URL}/api/oauth/org-status`, { headers: getAuthHeaders() });
    return response.data;
  },

  // Zoom OAuth
  zoom: {
    authorize: async (redirectPath?: string): Promise<AuthUrlResponse> => {
      const params = redirectPath ? { redirect_path: redirectPath } : {};
      const response = await axios.get(`${API_URL}/api/oauth/zoom/authorize`, { params, headers: getAuthHeaders() });
      return response.data;
    },
    disconnect: async (): Promise<DisconnectResponse> => {
      const response = await axios.delete(`${API_URL}/api/oauth/zoom/disconnect`, { headers: getAuthHeaders() });
      return response.data;
    },
  },

  // Google OAuth (Calendar, Meet, Drive)
  google: {
    authorize: async (scopes: string = 'calendar', redirectPath?: string): Promise<AuthUrlResponse> => {
      const params: Record<string, string> = { scopes };
      if (redirectPath) params.redirect_path = redirectPath;
      const response = await axios.get(`${API_URL}/api/oauth/google/authorize`, { params, headers: getAuthHeaders() });
      return response.data;
    },
    disconnect: async (): Promise<DisconnectResponse> => {
      const response = await axios.delete(`${API_URL}/api/oauth/google/disconnect`, { headers: getAuthHeaders() });
      return response.data;
    },
  },

  // Microsoft OAuth (Calendar, Teams, OneDrive)
  microsoft: {
    /**
     * @param scopes Comma list of features to request consent for — any of
     *   `email`, `calendar`, `contacts`, `meetings`. Omit to request all of them,
     *   which is what the Connected Services card needs.
     *
     *   Pass `'email'` from an email-only entry point: that asks for four scopes
     *   (User.Read, offline_access, Mail.ReadWrite, Mail.Send) instead of seven.
     *   Most Microsoft 365 tenants require an admin to approve the app, and
     *   requesting calendar/contacts/meetings *write* access just to send an email
     *   is the usual reason approval gets refused.
     */
    authorize: async (redirectPath?: string, scopes?: string): Promise<AuthUrlResponse> => {
      const params: Record<string, string> = {};
      if (redirectPath) params.redirect_path = redirectPath;
      if (scopes) params.scopes = scopes;
      const response = await axios.get(`${API_URL}/api/oauth/microsoft/authorize`, { params, headers: getAuthHeaders() });
      return response.data;
    },
    disconnect: async (): Promise<DisconnectResponse> => {
      const response = await axios.delete(`${API_URL}/api/oauth/microsoft/disconnect`, { headers: getAuthHeaders() });
      return response.data;
    },
  },

  // Asana OAuth
  asana: {
    authorize: async (redirectPath?: string): Promise<AuthUrlResponse> => {
      const params = redirectPath ? { redirect_path: redirectPath } : {};
      const response = await axios.get(`${API_URL}/api/oauth/asana/authorize`, { params, headers: getAuthHeaders() });
      return response.data;
    },
    disconnect: async (): Promise<DisconnectResponse> => {
      const response = await axios.delete(`${API_URL}/api/oauth/asana/disconnect`, { headers: getAuthHeaders() });
      return response.data;
    },
  },
};

// Provider labels and info
export const PROVIDER_INFO = {
  zoom: {
    name: 'Zoom',
    icon: 'Video',
    description: 'Video conferencing and meetings',
    features: ['Video meetings', 'Screen sharing', 'Recording'],
  },
  google: {
    name: 'Google',
    icon: 'Calendar',
    description: 'Google Calendar and Google Meet',
    features: ['Calendar sync', 'Google Meet', 'Google Drive'],
  },
  microsoft: {
    name: 'Microsoft 365',
    icon: 'Briefcase',
    description: 'Outlook Calendar and Microsoft Teams',
    features: ['Calendar sync', 'Teams meetings', 'OneDrive'],
  },
  asana: {
    name: 'Asana',
    icon: 'CheckSquare',
    description: 'Task and project management',
    features: ['Task sync', 'Project tracking', 'Team collaboration'],
  },
  jitsi: {
    name: 'Jitsi Meet',
    icon: 'Video',
    description: 'Open source video conferencing',
    features: ['Free', 'No account needed', 'Browser-based'],
  },
};

export default oauthAPI;
